import { motion, AnimatePresence } from 'motion/react';
import { Award, Zap, ShieldCheck, TrendingUp, BadgeCheck, Cpu, Target, Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useEffect, useMemo, useState } from 'react';

export default function HomeHeader() {
  const { user, activeWorkspace } = useAuth();

  const displayName = user?.displayName || user?.name || 'Diretor';
  const workspaceName = activeWorkspace?.name || 'Workspace Principal';

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const currentStage = activeWorkspace?.settings?.stage || 'Ideia';

  const getAiAdvice = (stage: string): string => {
    switch (stage) {
      case 'Ideia':
        return 'Ecossistema em fase inicial. Foque na validação de conceito e estruturação do roadmap.';
      case 'Validação':
        return 'Continue validando hipóteses. Otimize o feedback loop com dados reais dos stakeholders.';
      case 'Operação':
        return 'Empresa em operação. Monitore métricas financeiras e SLA de entregas dos projetos ativos.';
      case 'Escala':
        return 'Momento de escalar. Invista em automação e retenção de clientes para maximizar o crescimento.';
      default:
        return 'Ecossistema saudável. Foque nas tarefas e missões prioritárias do seu estágio.';
    }
  };

  const badgeItems = useMemo(() => {
    const items: { id?: string; title: string; category: string; level: string; description: string; earnedDate: string; icon: React.ElementType }[] = [];

    const stage = (currentStage || '').toLowerCase();
    if (['operação', 'operacao', 'escala', 'operação', 'escala'].some((s) => stage.includes(s))) {
      items.push({
        title: 'Líder Estratégico',
        category: 'Governança',
        level: 'Nível 5',
        description: 'Gestão e entrega contínua de mais de 10 projetos estratégicos no SLA acordado.',
        earnedDate: 'Q2 2026',
        icon: ShieldCheck,
      });
    }

    return items;
  }, [currentStage]);

  const [activeBadgeModal, setActiveBadgeModal] = useState<(typeof badgeItems)[0] | null>(null);

  useEffect(() => {
    if (!activeBadgeModal && badgeItems.length > 0) {
      setActiveBadgeModal(badgeItems[0]);
    }
  }, [badgeItems, activeBadgeModal]);

  return (
    <motion.header initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-3.5 pt-1 pb-1">
      {/* Executive Meta Ribbons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.4)]" />
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Workspace Ativo</span>
          <span className="text-slate-400 text-xs">/</span>
          <span className="text-[11px] text-white font-bold flex items-center gap-1.5">{workspaceName}</span>
        </div>

        <div className="flex items-center gap-3">
          <div className="px-2.5 py-0.5 bg-white/10 border border-white/10 rounded-lg flex items-center gap-1.5 shadow-2xs backdrop-blur-md">
            <Zap size={11} className="text-amber-400 fill-amber-400" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">Plano Enterprise</span>
          </div>
        </div>
      </div>

      {/* Greeting Title & Compact Side-by-Side AI Operational Intelligence Chip */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <h1 className="text-2xl sm:text-3xl font-display font-black text-white tracking-tight leading-none shrink-0">
          {getGreeting()}, <span className="text-slate-300 font-normal">{displayName}</span>.
        </h1>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="group flex items-center gap-2.5 bg-white/5 border border-white/10 hover:border-white/20 px-3 py-1.5 rounded-2xl shadow-2xs hover:shadow-xs transition-all duration-200 backdrop-blur-md"
        >
          <div className="w-5.5 h-5.5 rounded-lg bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300 shrink-0 shadow-2xs">
            <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase text-indigo-300 tracking-wider shrink-0 bg-indigo-500/20 px-1.5 py-0.5 rounded-md border border-indigo-400/30">IA</span>
            <p className="text-xs text-slate-200 font-medium leading-normal">{getAiAdvice(currentStage)}</p>
          </div>
        </motion.div>
      </div>

      {/* Executive User Achievement Badges Row */}
      {badgeItems.length > 0 && (
        <div className="flex flex-col gap-1.5 pt-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400/90 flex items-center gap-1.5">
              <Award size={12} className="text-slate-400" />
              Conquistas e Selos Executivos
            </span>
            <span className="text-[10px] text-slate-400/80 font-medium">{badgeItems.length} Conquista{ badgeItems.length !== 1 ? 's' : '' } Ativa{ badgeItems.length !== 1 ? 's' : '' }</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {badgeItems.map((badge, idx) => {
              const BadgeIcon = badge.icon;
              const baseBorder = 'border-slate-700/80 hover:border-slate-500/80';
              const baseBg = 'bg-slate-900/50 hover:bg-slate-800/80';
              const baseText = 'text-slate-200';
              const iconBg = 'bg-slate-800/80 text-slate-300 border border-slate-700/60';
              const tagBg = 'bg-slate-800/80 text-slate-300 border border-slate-700/50';

  return (
    <div
      key={`badge-${idx}`}
                  className={`group relative flex items-center gap-2 px-2.5 py-1.5 rounded-xl border ${baseBorder} ${baseBg} backdrop-blur-md transition-all duration-200 cursor-pointer hover:scale-[1.02] shadow-2xs`}
                  title={`${badge.title} - Clique para ver detalhes`}
                  onClick={() => setActiveBadgeModal(badge)}
                >
                  <div className={`w-6 h-6 rounded-lg ${iconBg} flex items-center justify-center shrink-0 shadow-2xs`}>
                    <BadgeIcon size={12} className="text-slate-300" />
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-semibold ${baseText} whitespace-nowrap`}>{badge.title}</span>
                    <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.2 rounded-md border ${tagBg} whitespace-nowrap`}>{badge.level}</span>
                  </div>

                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2.5 bg-slate-900/95 border border-slate-700/80 rounded-xl shadow-xl backdrop-blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-30 hidden sm:block">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-1.5 mb-1.5">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{badge.category}</span>
                      <span className="text-[10px] text-slate-400">{badge.earnedDate}</span>
                    </div>
                    <p className="text-[11px] text-slate-300 leading-snug font-normal">{badge.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail Modal for Selected Badge */}
      <AnimatePresence>
        {activeBadgeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl relative text-white"
            >
              <button
                onClick={() => setActiveBadgeModal(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {(() => {
                const ModalIcon = activeBadgeModal.icon;
                return (
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-800/80 text-slate-300 border border-slate-700/60 flex items-center justify-center shrink-0 shadow-md">
                      <ModalIcon size={24} className="text-slate-300" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-800 px-2 py-0.5 rounded-md border border-slate-700">
                          {activeBadgeModal.category}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400">Conquistado em {activeBadgeModal.earnedDate}</span>
                      </div>
                      <h3 className="text-lg font-bold text-white mt-1">{activeBadgeModal.title}</h3>
                    </div>
                  </div>
                );
              })()}

              <div className="p-3.5 bg-slate-800/60 border border-slate-700/60 rounded-xl mb-4">
                <p className="text-xs text-slate-300 leading-relaxed font-medium">{activeBadgeModal.description}</p>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-800 pt-3">
                <span className="flex items-center gap-1.5">
                  <Info size={13} className="text-slate-400" />
                  Nível de Governança
                </span>
                <span className="font-bold text-slate-200">{activeBadgeModal.level}</span>
              </div>

              <button
                onClick={() => setActiveBadgeModal(null)}
                className="w-full mt-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-bold text-xs rounded-xl transition-colors cursor-pointer border border-slate-700"
              >
                Fechar Detalhes
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
